import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { 
  Users, User, X, Check, Loader2, Search, FolderOpen, ChevronRight
} from 'lucide-react'
import toast from 'react-hot-toast'
import { 
  getProspectGroups, 
  getMyProspects,
  enrollProspectsInSequence,
  enrollGroupInSequence
} from '../../services/api'

export default function EnrollProspectsModal({ sequence, onClose, onSuccess }) {
  const queryClient = useQueryClient()
  const [mode, setMode] = useState('groups') // 'groups' | 'individual'
  const [selectedGroups, setSelectedGroups] = useState([])
  const [selectedProspects, setSelectedProspects] = useState([])
  const [search, setSearch] = useState('')

  // Fetch groups
  const { data: groupsData, isLoading: loadingGroups } = useQuery({
    queryKey: ['prospectGroups'],
    queryFn: getProspectGroups,
    enabled: mode === 'groups'
  })

  const groups = groupsData?.member || groupsData?.['hydra:member'] || []

  // Fetch prospects
  const { data: prospectsData, isLoading: loadingProspects } = useQuery({
    queryKey: ['myProspects'],
    queryFn: getMyProspects,
    enabled: mode === 'individual'
  })

  const prospects = prospectsData?.prospects || prospectsData || []

  // Filter prospects by search
  const filteredProspects = prospects.filter(p => {
    if (!search) return true
    const searchLower = search.toLowerCase()
    return (
      p.firstName?.toLowerCase().includes(searchLower) ||
      p.lastName?.toLowerCase().includes(searchLower) ||
      p.primaryEmail?.toLowerCase().includes(searchLower) ||
      p.companyName?.toLowerCase().includes(searchLower)
    )
  })

  // Enroll mutation
  const enrollMutation = useMutation({
    mutationFn: async () => {
      let totalEnrolled = 0
      let errors = []
      
      // Enroll groups
      for (const groupId of selectedGroups) {
        try {
          const result = await enrollGroupInSequence(sequence.id, groupId)
          totalEnrolled += result.enrolled || 0
        } catch (err) {
          console.error('Error enrolling group:', err)
          errors.push(err.response?.data?.detail || err.message || 'Erreur groupe')
        }
      }
      
      // Enroll individual prospects
      if (selectedProspects.length > 0) {
        try {
          const result = await enrollProspectsInSequence(sequence.id, selectedProspects)
          totalEnrolled += result.enrolled || 0
        } catch (err) {
          console.error('Error enrolling prospects:', err)
          errors.push(err.response?.data?.detail || err.message || 'Erreur prospects')
        }
      }
      
      // If all calls failed, throw an error
      if (errors.length > 0 && totalEnrolled === 0) {
        throw new Error(errors.join(', '))
      }
      
      return { totalEnrolled, errors }
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['sequenceEnrollments', sequence.id] })
      
      if (data.totalEnrolled > 0) {
        toast.success(`${data.totalEnrolled} prospect(s) ajouté(s) à la séquence`)
      }
      if (data.errors?.length > 0) {
        toast.error(`Erreurs: ${data.errors.join(', ')}`)
      }
      
      onSuccess && onSuccess()
      onClose()
    },
    onError: (err) => toast.error(err.message || 'Erreur lors de l\'ajout')
  })

  const toggleGroup = (id) => {
    setSelectedGroups(prev => 
      prev.includes(id) ? prev.filter(g => g !== id) : [...prev, id]
    )
  }

  const toggleProspect = (id) => {
    setSelectedProspects(prev => 
      prev.includes(id) ? prev.filter(p => p !== id) : [...prev, id]
    )
  }

  const totalSelected = selectedGroups.length + selectedProspects.length
  const isLoading = mode === 'groups' ? loadingGroups : loadingProspects

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      background: 'rgb(var(--rgb-shade) / 0.8)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      <div style={{
        background: 'rgb(var(--rgb-surface-hover))',
        borderRadius: '16px',
        padding: '2rem',
        width: '100%',
        maxWidth: '650px',
        maxHeight: '85vh',
        display: 'flex',
        flexDirection: 'column',
        border: '1px solid rgb(var(--rgb-ink) / 0.1)'
      }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <div>
            <h2 style={{ fontSize: '1.25rem', fontWeight: 600, marginBottom: '0.25rem' }}>
              Ajouter à "{sequence.name}"
            </h2>
            <p style={{ color: 'var(--color-text-secondary)', fontSize: '0.9rem' }}>
              Sélectionnez des groupes ou des prospects individuels
            </p>
          </div>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: 'var(--color-text-secondary)', cursor: 'pointer' }}>
            <X size={24} />
          </button>
        </div>

        {/* Mode tabs */}
        <div style={{ 
          display: 'flex', 
          gap: '0.5rem', 
          marginBottom: '1rem',
          background: 'rgb(var(--rgb-ink) / 0.05)',
          padding: '0.25rem',
          borderRadius: '8px'
        }}>
          <button
            onClick={() => setMode('groups')}
            style={{
              flex: 1,
              padding: '0.75rem',
              background: mode === 'groups' ? 'rgba(91, 92, 244,0.2)' : 'transparent',
              border: 'none',
              borderRadius: '6px',
              color: mode === 'groups' ? '#5b5cf4' : 'var(--color-text-secondary)',
              cursor: 'pointer',
              fontWeight: 500,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem'
            }}
          >
            <FolderOpen size={18} />
            Groupes
            {selectedGroups.length > 0 && (
              <span style={{
                background: '#5b5cf4',
                color: '#fff',
                padding: '0.1rem 0.5rem',
                borderRadius: '10px',
                fontSize: '0.75rem',
                fontWeight: 600
              }}>
                {selectedGroups.length}
              </span>
            )}
          </button>
          <button
            onClick={() => setMode('individual')}
            style={{
              flex: 1,
              padding: '0.75rem',
              background: mode === 'individual' ? 'rgba(91, 92, 244,0.2)' : 'transparent',
              border: 'none',
              borderRadius: '6px',
              color: mode === 'individual' ? '#5b5cf4' : 'var(--color-text-secondary)',
              cursor: 'pointer',
              fontWeight: 500,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem'
            }}
          >
            <User size={18} />
            Prospects individuels
            {selectedProspects.length > 0 && (
              <span style={{
                background: '#5b5cf4',
                color: '#fff',
                padding: '0.1rem 0.5rem',
                borderRadius: '10px',
                fontSize: '0.75rem',
                fontWeight: 600
              }}>
                {selectedProspects.length}
              </span>
            )}
          </button>
        </div>

        {/* Search (for individual mode) */}
        {mode === 'individual' && (
          <div style={{ position: 'relative', marginBottom: '1rem' }}>
            <Search size={18} style={{ 
              position: 'absolute', 
              left: '0.75rem', 
              top: '50%', 
              transform: 'translateY(-50%)',
              color: 'var(--color-text-muted)'
            }} />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher par nom, email, entreprise..."
              style={{
                width: '100%',
                padding: '0.75rem 0.75rem 0.75rem 2.5rem',
                background: 'rgb(var(--rgb-ink) / 0.05)',
                border: '1px solid rgb(var(--rgb-ink) / 0.1)',
                borderRadius: '8px',
                color: 'var(--color-text)',
                fontSize: '1rem'
              }}
            />
          </div>
        )}

        {/* Content */}
        <div style={{ flex: 1, overflowY: 'auto', marginBottom: '1rem' }}>
          {isLoading ? (
            <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--color-text-secondary)' }}>
              <Loader2 size={24} style={{ animation: 'spin 1s linear infinite' }} />
            </div>
          ) : mode === 'groups' ? (
            // Groups list
            groups.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--color-text-secondary)' }}>
                <FolderOpen size={40} style={{ marginBottom: '1rem', opacity: 0.5 }} />
                <p>Aucun groupe créé</p>
                <p style={{ fontSize: '0.85rem' }}>Créez des groupes dans la section Prospects</p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {groups.map((group) => (
                  <div
                    key={group.id}
                    onClick={() => toggleGroup(group.id)}
                    style={{
                      padding: '1rem',
                      background: selectedGroups.includes(group.id) 
                        ? 'rgba(91, 92, 244,0.1)' 
                        : 'rgb(var(--rgb-ink) / 0.02)',
                      border: selectedGroups.includes(group.id)
                        ? '1px solid rgba(91, 92, 244,0.3)'
                        : '1px solid rgb(var(--rgb-ink) / 0.1)',
                      borderRadius: '10px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '1rem'
                    }}
                  >
                    <div style={{
                      width: '22px',
                      height: '22px',
                      borderRadius: '6px',
                      border: selectedGroups.includes(group.id) 
                        ? '2px solid #5b5cf4' 
                        : '2px solid rgb(var(--rgb-ink) / 0.3)',
                      background: selectedGroups.includes(group.id) ? '#5b5cf4' : 'transparent',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}>
                      {selectedGroups.includes(group.id) && (
                        <Check size={14} style={{ color: '#0a0a0a' }} />
                      )}
                    </div>
                    <div style={{
                      width: '36px',
                      height: '36px',
                      borderRadius: '8px',
                      background: `${group.color || '#3b82f6'}20`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}>
                      <Users size={18} style={{ color: group.color || '#3b82f6' }} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 500 }}>{group.name}</div>
                      <div style={{ color: 'var(--color-text-secondary)', fontSize: '0.85rem' }}>
                        {group.prospectCount || 0} prospect{(group.prospectCount || 0) > 1 ? 's' : ''}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )
          ) : (
            // Individual prospects list
            filteredProspects.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--color-text-secondary)' }}>
                <User size={40} style={{ marginBottom: '1rem', opacity: 0.5 }} />
                <p>Aucun prospect trouvé</p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                {filteredProspects.map((prospect) => (
                  <div
                    key={prospect.id}
                    onClick={() => toggleProspect(prospect.id)}
                    style={{
                      padding: '0.75rem 1rem',
                      background: selectedProspects.includes(prospect.id) 
                        ? 'rgba(91, 92, 244,0.1)' 
                        : 'rgb(var(--rgb-ink) / 0.02)',
                      border: selectedProspects.includes(prospect.id)
                        ? '1px solid rgba(91, 92, 244,0.3)'
                        : '1px solid rgb(var(--rgb-ink) / 0.1)',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.75rem'
                    }}
                  >
                    <div style={{
                      width: '20px',
                      height: '20px',
                      borderRadius: '4px',
                      border: selectedProspects.includes(prospect.id) 
                        ? '2px solid #5b5cf4' 
                        : '2px solid rgb(var(--rgb-ink) / 0.3)',
                      background: selectedProspects.includes(prospect.id) ? '#5b5cf4' : 'transparent',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}>
                      {selectedProspects.includes(prospect.id) && (
                        <Check size={14} style={{ color: '#0a0a0a' }} />
                      )}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 500 }}>
                        {prospect.firstName} {prospect.lastName}
                      </div>
                      <div style={{ color: 'var(--color-text-secondary)', fontSize: '0.85rem' }}>
                        {prospect.primaryEmail} {prospect.companyName && `• ${prospect.companyName}`}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )
          )}
        </div>

        {/* Footer */}
        <div style={{ 
          display: 'flex', 
          gap: '1rem', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          paddingTop: '1rem',
          borderTop: '1px solid rgb(var(--rgb-ink) / 0.1)'
        }}>
          <span style={{ color: 'var(--color-text-secondary)', fontSize: '0.9rem' }}>
            {totalSelected} sélectionné(s)
          </span>
          <div style={{ display: 'flex', gap: '1rem' }}>
            <button
              onClick={onClose}
              style={{
                padding: '0.75rem 1.5rem',
                background: 'rgb(var(--rgb-ink) / 0.1)',
                border: 'none',
                borderRadius: '8px',
                color: 'var(--color-text)',
                cursor: 'pointer',
                fontWeight: 500
              }}
            >
              Annuler
            </button>
            <button
              onClick={() => enrollMutation.mutate()}
              disabled={totalSelected === 0 || enrollMutation.isPending}
              style={{
                padding: '0.75rem 1.5rem',
                background: '#5b5cf4',
                border: 'none',
                borderRadius: '8px',
                color: '#fff',
                cursor: totalSelected === 0 ? 'not-allowed' : 'pointer',
                fontWeight: 600,
                opacity: totalSelected === 0 ? 0.5 : 1,
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}
            >
              {enrollMutation.isPending ? (
                <>
                  <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
                  Ajout...
                </>
              ) : (
                <>Ajouter à la séquence</>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
